import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import uniq from 'lodash/uniq'

import { SINGLE_VALUE_IN_ARRAY } from 'Core/constants/defaultValues'

export const isLastIndex = <T extends unknown>(array: T[], index: number) =>
	index + 1 === array.length

export const entitiesArrayToStringByNames = (array: Entity[]) =>
	array.reduce((acc, item) => (!acc ? `${item.name}` : `${acc},${item.name}`), '')

export const defaultSort = (a: number | string | null, b: number | string | null) => {
	if (isNull(a) && isNull(b)) {
		return 0
	} else if (isNull(a)) {
		return -1
	} else if (isNull(b)) {
		return 1
	}

	if (a < b) {
		return -1
	}

	if (b < a) {
		return 1
	}
	return 0
}

export const addItemToArrayBeginning = <Item extends {}>(array: Item[], item: Item): Item[] => [
	{ ...item },
	...array
]

export const updateItemInArray = <Item extends { id: Key }>(
	array: Item[],
	updatedItem: Partial<Item>
): Item[] =>
	array.reduce(
		(acc, item) =>
			item.id !== updatedItem.id ? [...acc, item] : [...acc, { ...item, ...updatedItem }],
		[] as Item[]
	)

export const deleteItemByIdFromArray = <Item extends { id: Key }>(array: Item[], id: Key) =>
	array.filter((item) => item.id !== id)

export const insertToArrayConditionally = <T extends unknown>(
	condition: boolean,
	...elements: T[]
) => (condition ? elements : [])

export const checkIsFieldChanged = <T extends {}>(
	filedList: Array<keyof T>,
	field?: keyof T
): boolean => {
	if (!field) {
		return false
	}

	return filedList.includes(field)
}

export const range = (from: number, to: number) =>
	Array.from({ length: to - from + 1 }, (_, index) => from + index)

export const removeFromArray = <T>(array: T[], element: T) => {
	return array.filter((i) => i !== element)
}

export const mergeCurrentWithNewArrayData = <
	currentData extends { uniqId: BasicNumber; id: number }[],
	newData extends { uniqId: BasicNumber; id: number }[]
>(
	currentArray: currentData,
	newArray: newData
): Array<{
	uniqId: number
	id: BasicNumber
	new: newData[number] | null
	current: currentData[number] | null
}> => {
	const newIds = newArray.map((item) => item.uniqId).filter((id) => !isNull(id)) as number[]
	const currentIds: number[] = currentArray
		.map((item) => item.uniqId)
		.filter((id) => !isNull(id)) as number[]
	return uniq([...newIds, ...currentIds]).map((uniqId) => {
		const newItem = newArray.find((item) => item.uniqId === uniqId) || null
		const currentItem = currentArray.find((item) => item.uniqId === uniqId) || null
		return {
			id: currentItem?.id || newItem?.id || null,
			uniqId,
			current: currentItem,
			new: newItem
		}
	})
}

export const doesSingleOptionIsEmpty = <T extends Key>(options: T[]) => {
	return options.length === SINGLE_VALUE_IN_ARRAY && isEmpty(options[0])
}

export const hasSingleItem = <T>(array: T[]) => {
	return array.length === SINGLE_VALUE_IN_ARRAY
}

export const excludeTail = <T>(array: T[]) => array.slice(0, -1)
